import { Component } from '@theme/component';

/**
 * @typedef {Object} TechTabsRefs
 * @property {HTMLElement} bar - Container the tab buttons are rendered into.
 * @property {HTMLElement} body - Container holding the panels.
 */

/**
 * Tabbed card built from the panels already present in the markup.
 *
 * Progressive enhancement: with no JavaScript every panel stays visible and the
 * bar stays empty, so the content is always reachable. On upgrade the bar is
 * filled from each panel's `data-tab-*` attributes and the WAI-ARIA tab pattern
 * takes over.
 *
 * @extends {Component<TechTabsRefs>}
 */
class TechTabs extends Component {
  /** @type {HTMLButtonElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  connectedCallback() {
    super.connectedCallback();
    this.#build();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#tabs = [];
    this.#panels = [];
  }

  /**
   * Selects one tab and reveals its panel.
   * @param {number} index - Index of the tab to select.
   * @param {boolean} [moveFocus] - Whether keyboard focus follows the selection.
   */
  select(index, moveFocus = false) {
    if (index < 0 || index >= this.#tabs.length) return;

    for (const [i, tab] of this.#tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      this.#panels[i].hidden = !selected;
    }

    if (moveFocus) this.#tabs[index].focus();
  }

  #build() {
    const bar = this.refs.bar ?? this.querySelector('.tech-tabs__bar');
    const panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-tech-panel]')]);

    // One panel needs no tab bar, and no bar means nothing to enhance — in both
    // cases the panels stay visible, which is the no-JavaScript state anyway.
    if (!(bar instanceof HTMLElement) || panels.length < 2) return;

    this.#panels = panels;
    bar.replaceChildren();
    bar.setAttribute('role', 'tablist');

    const label = this.dataset.tablistLabel;
    if (label) bar.setAttribute('aria-label', label);

    for (const [i, panel] of panels.entries()) {
      const id = `${this.id || 'TechTabs'}-${i}`;
      panel.id = `${id}-panel`;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', `${id}-tab`);
      panel.tabIndex = 0;

      bar.append(this.#createTab(panel, id, i));
    }

    this.#tabs = /** @type {HTMLButtonElement[]} */ ([...bar.children]);
    this.select(0);
  }

  /**
   * @param {HTMLElement} panel - The panel this tab controls.
   * @param {string} id - Shared id stem for the tab / panel pair.
   * @param {number} index - Position in the tab list.
   * @returns {HTMLButtonElement}
   */
  #createTab(panel, id, index) {
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'tech-tabs__tab';
    tab.id = `${id}-tab`;
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-controls', panel.id);

    const stack = document.createElement('span');

    if (panel.dataset.tabKicker) {
      const kicker = document.createElement('span');
      kicker.className = 'tech-tabs__kicker';
      kicker.textContent = panel.dataset.tabKicker;
      stack.append(kicker);
    }

    const name = document.createElement('span');
    name.className = 'tech-tabs__label';
    name.textContent = panel.dataset.tabLabel || '';
    stack.append(name);
    tab.append(stack);

    if (panel.dataset.tabPage) {
      const page = document.createElement('span');
      page.className = 'tech-tabs__page';
      page.textContent = panel.dataset.tabPage;
      tab.append(page);
    }

    tab.addEventListener('click', () => this.select(index));
    tab.addEventListener('keydown', (event) => this.#handleKeydown(event, index));

    return tab;
  }

  /**
   * @param {KeyboardEvent} event - The originating key event.
   * @param {number} index - Index of the tab the key was pressed on.
   */
  #handleKeydown(event, index) {
    const last = this.#tabs.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next, true);
  }
}

if (!customElements.get('tech-tabs')) {
  customElements.define('tech-tabs', TechTabs);
}
